/**
 * General section of the role create/edit form.
 */
import { Shield } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { Select, type SelectOption } from '@/components/forms/Select';
import type { RoleFormState, RoleMode, RoleStatus } from '@/types/role.types';
import {
  ROLE_FORM_MODE_OPTIONS,
  ROLE_FORM_STATUS_OPTIONS,
  normalizeRoleCode,
} from '@/components/roles/role-form-utils';

/** Props for {@link RoleGeneralSection}. */
interface RoleGeneralSectionProps {
  form: RoleFormState;
  onChange: (field: keyof RoleFormState, value: unknown) => void;
  groupOptions: SelectOption[];
  showCode?: boolean;
}

/** General section of the role create/edit form. */
export function RoleGeneralSection({
  form,
  onChange,
  groupOptions,
  showCode = false,
}: Readonly<RoleGeneralSectionProps>) {
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Shield className="w-4 h-4" />
        Informations générales
      </h4>
      {showCode ? (
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Code"
            required
            value={form.code}
            onChange={(e) => onChange('code', normalizeRoleCode(e.target.value))}
            placeholder="EX: PROFESSEUR"
          />
          <Input
            label="Libellé"
            required
            value={form.label}
            onChange={(e) => onChange('label', e.target.value)}
            placeholder="Ex: Professeur"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          <Input
            label="Libellé"
            required
            value={form.label}
            onChange={(e) => onChange('label', e.target.value)}
            placeholder="Ex: Professeur"
          />
        </div>
      )}
      <div className="grid grid-cols-1 gap-4">
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="Description du rôle (optionnel)"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Groupe de rôles"
          value={form.roleGroup}
          onChange={(value) => onChange('roleGroup', value)}
          options={groupOptions}
          placeholder="Sélectionner un groupe"
        />
        <Input
          label="Niveau hiérarchique"
          type="number"
          value={form.hierarchyLevel}
          onChange={(e) => onChange('hierarchyLevel', e.target.value)}
          placeholder="Ex: 10"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Statut"
          value={form.status}
          onChange={(value) => onChange('status', value as RoleStatus)}
          options={ROLE_FORM_STATUS_OPTIONS}
        />
        <Select
          label="Mode opérationnel"
          value={form.mode}
          onChange={(value) => onChange('mode', value as '' | RoleMode)}
          options={ROLE_FORM_MODE_OPTIONS}
        />
      </div>
    </div>
  );
}
